import React from 'react';
import colors from '../utils/adminColors';
import { pluralize } from '../utils/pluralize';

/**
 * Opportunities briefing run-status line for the admin PO-upload card
 * (Lane 37 build step 3 §4). Renders NOTHING when there is no status to show
 * (module flag OFF → the caller keeps `status` null; or an unknown/none state),
 * so the upload screen is unchanged until a generation has run.
 *
 * Pure view mapping is exported separately so it is unit-testable without a DOM.
 *
 * @param {{state: string, updated_at?: string|null, finding_count?: number|null}} status
 * @param {(d: string) => string} [formatDate] optional date formatter
 */
export function opportunityStatusView(status, formatDate) {
  if (!status || !status.state) return null;
  const fmt = (d) => (d ? (formatDate ? formatDate(d) : String(d)) : '');
  const when = status.updated_at ? fmt(status.updated_at) : '';
  switch (status.state) {
    case 'in_progress':
      return { tone: colors.textMuted, text: 'Briefing generation: in progress…' };
    case 'succeeded': {
      const n = status.finding_count ?? 0;
      return {
        tone: colors.success,
        text: `Briefing generation: succeeded, ${pluralize(n, 'opportunity', 'opportunities')} found${when ? ` · ${when}` : ''}`,
      };
    }
    case 'failed':
      return {
        tone: colors.danger,
        text: `Briefing generation: failed${when ? ` ${when}` : ''}. Needs attention`,
      };
    default:
      return null; // 'none' or anything unexpected → render nothing
  }
}

export default function OpportunityStatusLine({ status, formatDate }) {
  const view = opportunityStatusView(status, formatDate);
  if (!view) return null;
  return (
    <div
      data-testid="opportunity-status-line"
      style={{
        marginTop: '10px',
        fontSize: 'var(--font-size-xs)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        color: view.tone,
      }}
    >
      {view.text}
    </div>
  );
}
